import React, { createContext, useContext, ReactNode, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthProvider';
import { billingService } from '../../features/dashboard_user/services/billing.service';
import { SubscriptionStatus } from '../../features/dashboard_user/types/billing.types';

interface SubscriptionContextType {
  subscriptionStatus: SubscriptionStatus | null;
  isLoading: boolean;
  refreshSubscription: () => Promise<void>;
  showUpgradeModal: () => void;
  hideUpgradeModal: () => void;
  isUpgradeModalOpen: boolean;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { authState } = useAuth();
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

  const fetchSubscriptionStatus = useCallback(async () => {
    if (!authState.isAuthenticated) {
      setSubscriptionStatus(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const status = await billingService.getSubscriptionStatus();
      setSubscriptionStatus(status);
    } catch (error) {
      console.error('Error fetching subscription status:', error);
      // If user doesn't have a subscription, status will be null
      setSubscriptionStatus(null);
    } finally {
      setIsLoading(false);
    }
  }, [authState.isAuthenticated]);

  useEffect(() => {
    if (authState.isAuthenticated && !authState.isLoading) {
      fetchSubscriptionStatus();
    } else if (!authState.isAuthenticated) {
      setSubscriptionStatus(null);
      setIsLoading(false);
    }
  }, [authState.isAuthenticated, authState.isLoading, fetchSubscriptionStatus]);

  // Listen for custom event to show upgrade modal
  useEffect(() => {
    const handleShowUpgradeModal = () => {
      setIsUpgradeModalOpen(true);
    };

    window.addEventListener('showUpgradeModal', handleShowUpgradeModal);
    return () => {
      window.removeEventListener('showUpgradeModal', handleShowUpgradeModal);
    };
  }, []);

  const showUpgradeModal = useCallback(() => {
    setIsUpgradeModalOpen(true);
  }, []);

  const hideUpgradeModal = useCallback(() => {
    setIsUpgradeModalOpen(false);
  }, []);

  return (
    <SubscriptionContext.Provider
      value={{
        subscriptionStatus,
        isLoading,
        refreshSubscription: fetchSubscriptionStatus,
        showUpgradeModal,
        hideUpgradeModal,
        isUpgradeModalOpen,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return context;
};
